import CancellableHelper from "@/requests/CancellableHelper";
import { MailBoxListItemsResponse } from "@/models/api/administration/mailBox/mailBoxListItem";
import { MailConfiguration } from "@/models/api/administration/mailBox/mailConfiguration";
import { MailBox } from "@/models/api/mail/NotificationData";
import { MAIL_TYPE_ENUM } from "@/static/mailTypeEnum";
import { MailContentKey } from "@/models/api/administration/mailBox/mailContentKey";
import { MailContentModel } from "@/models/api/administration/mailBox/mailContentModel";
import { MailContent } from "@/models/api/administration/mailBox/mailContent";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { MailHistoryResponse } from "@/models/api/administration/mailBox/mailHistory";
import { MailResponse } from "@/models/api/administration/mailBox/mail";
import { MailHistoryContent } from "@/models/api/administration/mailBox/mailHistoryContent";
import { PairMailMultiple } from "@/models/api/administration/mailBox/PairMailMultiple";
import { BookingToPair } from "@/models/api/administration/mailBox/BookingToPair";

//#region Routes
export const URL_GET_MAIL_BOX_LIST = "/MailBox/GetMailBoxList";
export const URL_GET_MAIL_CONFIGURATION_MODEL = "/MailBox/GetMailConfigurationModel";
export const URL_POST_RUN_TEST_CONNECTION = "/MailBox/RunTestConnection";
export const URL_GET_LAST_MAIL_UID_MESSAGE = "/MailBox/GetLastMailUIDMessage";
export const URL_POST_SAVE_MAIL_CONFIGURATION = "/MailBox/SaveMailConfiguration";
export const URL_POST_CREATE_MAIL_CONFIGURATION = "/MailBox/CreateMailConfiguration";
export const URL_GET_MAIL_BOXES = "/MailBox/List";
export const URL_GET_MAIL_BOX_LIST_FOR_CONTENT = "/MailBox/GetMailboxListForContent";
export const URL_GET_MAIL_CONTENT_KEY_LIST = "/MailBox/GetMailContentKeyList";
export const URL_GET_CONTRACTOR_MAIL_CONTENT_KEY_LIST = "/MailBox/GetContractorMailContentKeyList";
export const URL_GET_MAIL_CONTENT = "/MailBox/GetMailContent";
export const URL_POST_SAVE_MAIL_CONTENT = "/MailBox/SaveMailContent";
export const URL_POST_DELETE_MAIL_CONTENT = "/MailBox/DeleteMailContent";
export const URL_GET_MAIL_HISTORY = "/MailBox/GetMailHistory";
export const URL_GET_MAIL_HISTORY_CONTENT = "/MailBox/MailHistory/${MailHistoryId}/Content";
export const URL_GET_MAIL_LIST = "/MailBox/GetMailList";
export const URL_POST_PAIR_MAIL = "/MailBox/PairMail";
export const URL_GET_BOOKINGS_TO_PAIR_WITH = "/MailBox/GetBookingsToPairWith";
export const URL_POST_PAIR_MAIL_WITH_MULTIPLE_BOOKINGS = "/MailBox/PairMailWithMultipleBookings";
//#endregion

const _getMailBoxList = new CancellableHelper<MailBoxListItemsResponse>(
  URL_GET_MAIL_BOX_LIST,
  MailBoxListItemsResponse
);
export const getMailBoxList = async (page: number, count: number): Promise<MailBoxListItemsResponse> => {
  return await _getMailBoxList.get({ page: page, count: count });
};

const _getMailConfiguration = new CancellableHelper<MailConfiguration>(
  URL_GET_MAIL_CONFIGURATION_MODEL,
  MailConfiguration
);
export const getMailConfiguration = async (mailId: number): Promise<MailConfiguration> => {
  return await _getMailConfiguration.get({ mailId: mailId });
};

const _lastReadMailUIDMessage = new CancellableHelper<null>(URL_GET_LAST_MAIL_UID_MESSAGE, null, true);
export const lastReadMailUIDMessage = async (mailConfigurationId: number, connectionType: string): Promise<null> => {
  return await _lastReadMailUIDMessage.post(
    {
      mailConfigurationId: mailConfigurationId,
      connectionType: connectionType,
    },
    null
  );
};

const _postRunTestConnection = new CancellableHelper<null>(URL_POST_RUN_TEST_CONNECTION, null, true);
export const runTestConnection = async (mailConfigurationId: number, connectionType: string): Promise<null> => {
  return await _postRunTestConnection.post(
    {
      mailConfigurationId: mailConfigurationId,
      connectionType: connectionType,
    },
    null
  );
};

const _saveMailConfiguration = new CancellableHelper<null>(URL_POST_SAVE_MAIL_CONFIGURATION, null, true);
export const saveMailConfiguration = async (mailConfiguration: MailConfiguration): Promise<null> => {
  return await _saveMailConfiguration.post(
    {
      mailConfiguration: mailConfiguration,
    },
    null
  );
};

const _createMailConfiguration = new CancellableHelper<null>(URL_POST_CREATE_MAIL_CONFIGURATION, null, true);
export const createMailConfiguration = async (mailConfiguration: MailConfiguration): Promise<null> => {
  return await _createMailConfiguration.post(
    {
      mailConfiguration: mailConfiguration,
    },
    null
  );
};

const _getMailBoxes = new CancellableHelper<Array<MailBox>>(URL_GET_MAIL_BOXES, MailBox);
export const getMailBoxes = async (mailTypeEnum: MAIL_TYPE_ENUM): Promise<Array<MailBox>> => {
  return _getMailBoxes.get({ MailTypeEnum: mailTypeEnum });
};

const _getMailBoxListForContent = new CancellableHelper<Array<MailBox>>(URL_GET_MAIL_BOX_LIST_FOR_CONTENT, MailBox);
export const getMailBoxListForContent = async (): Promise<Array<MailBox>> => {
  return await _getMailBoxListForContent.get();
};

const _getMailContentKeyList = new CancellableHelper<Array<MailContentKey>>(
  URL_GET_MAIL_CONTENT_KEY_LIST,
  MailContentKey
);
export const getMailContentKeyList = async (): Promise<Array<MailContentKey>> => {
  return await _getMailContentKeyList.get();
};

const _getContractorMailContentKeyList = new CancellableHelper<Array<MailContentKey>>(
  URL_GET_CONTRACTOR_MAIL_CONTENT_KEY_LIST,
  MailContentKey
);
export const getContractorMailContentKeyList = async (): Promise<Array<MailContentKey>> => {
  return await _getContractorMailContentKeyList.get();
};

const _getMailContent = new CancellableHelper<MailContentModel>(URL_GET_MAIL_CONTENT, MailContentModel);
export const getMailContent = async (mailContent: MailContent): Promise<MailContentModel> => {
  return await _getMailContent.get({ MailContent: mailContent });
};

const _postSaveMailContent = new CancellableHelper<null>(URL_POST_SAVE_MAIL_CONTENT, null, true);
export const postSaveMailContent = async (mailContent: MailContent): Promise<null> => {
  return await _postSaveMailContent.post(null, mailContent);
};

const _postDeleteMailContent = new CancellableHelper<null>(URL_POST_DELETE_MAIL_CONTENT, null, true);
export const postDeleteMailContent = async (mailContent: MailContent): Promise<null> => {
  return await _postDeleteMailContent.post({ MailContent: mailContent }, null);
};

const _getMailHistory = new CancellableHelper<MailHistoryResponse>(URL_GET_MAIL_HISTORY, MailHistoryResponse);
_getMailHistory.jsonSerializer = true;
export const getMailHistory = async (genericTableRequest: GenericTableRequest): Promise<MailHistoryResponse> => {
  return await _getMailHistory.get(genericTableRequest);
};

const _getMailHistoryContent = new CancellableHelper<MailHistoryContent>(
  URL_GET_MAIL_HISTORY_CONTENT,
  MailHistoryContent
);
export const getMailHistoryContent = async (mailHistoryId: number): Promise<MailHistoryContent> => {
  return _getMailHistoryContent.get({ MailHistoryId: mailHistoryId });
};

const _getMailList = new CancellableHelper<MailResponse>(URL_GET_MAIL_LIST, MailResponse);
_getMailList.jsonSerializer = true;
export const getMailList = async (genericTableRequest: GenericTableRequest): Promise<MailResponse> => {
  return await _getMailList.get(genericTableRequest);
};

const _postPairMail = new CancellableHelper<null>(URL_POST_PAIR_MAIL, null, true);
export const postPairMail = async (mailId: number, mailboxType: number): Promise<null> => {
  return await _postPairMail.post({ MailId: mailId, MailboxType: mailboxType }, null);
};

const _getBookingsToPairWith = new CancellableHelper<Array<BookingToPair>>(
  URL_GET_BOOKINGS_TO_PAIR_WITH,
  BookingToPair
);
export const getBookingsToPairWith = async (number: string): Promise<Array<BookingToPair>> => {
  return await _getBookingsToPairWith.get({ Number: number });
};

const _pairMailWithMultipleBookings = new CancellableHelper<null>(
  URL_POST_PAIR_MAIL_WITH_MULTIPLE_BOOKINGS,
  null,
  true
);
export const pairMailWithMultipleBookings = async (model: PairMailMultiple): Promise<null> => {
  return await _pairMailWithMultipleBookings.post(null, model);
};
